import { type FC, useMemo } from 'react';

import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  IconButton,
  Text,
} from '@chakra-ui/react';
import { type OptionBase } from 'chakra-react-select';

import { Select } from '@/components/Select';

import { IconLeftTailedArrow, IconRightTailedArrow } from '../Icons';

const pageSizes = [10, 25, 50] as const;
type TPageSize = (typeof pageSizes)[number];

export type { TPageSize as FullScopePaginationPageSizeType };

export { pageSizes as fullScopePaginationPageSizes };

interface PageSizeOption extends OptionBase {
  label: string;
  value: TPageSize;
}

type FullScopePaginationProps = {
  totalItems: number;
  page: number;
  setPage: (newPage: number) => void;
  pageSize: TPageSize;
  setPageSize: (newPageSize: TPageSize) => void;
};

const toOptions = (pageSizeOption: TPageSize): PageSizeOption => {
  return {
    label: `${pageSizeOption} per page`,
    value: pageSizeOption,
  };
};

export const FullScopePagination: FC<FullScopePaginationProps> = ({
  totalItems,
  page,
  setPage,
  pageSize,
  setPageSize,
}) => {
  const rangeStart = useMemo(() => {
    return (page - 1) * pageSize || 1;
  }, [page, pageSize]);

  const rangeEnd = useMemo(() => {
    const t = page * pageSize;

    if (t >= totalItems) {
      return totalItems;
    } else {
      return t;
    }
  }, [page, pageSize, totalItems]);

  return (
    <HStack gap={4}>
      <Box minW="140px">
        <Select
          aria-label="page size"
          placeholder="per page"
          value={pageSizes.map(toOptions).find((o) => o.value === pageSize)}
          onChange={(o) => {
            if (o) {
              setPageSize(o.value);
              setPage(1);
            }
          }}
          options={pageSizes.map(toOptions)}
          size="md"
          menuPlacement="top"
        />
      </Box>

      <ButtonGroup size="md" isAttached variant="outline">
        <Button _hover={{}} _active={{}} cursor="auto" minW="134px">
          <HStack gap={1} justify="start" w="full">
            <Text color="gray.900" fontSize="md" fontWeight="medium">
              {rangeStart}-{rangeEnd}
            </Text>
            <Text color="gray.500" fontSize="md" fontWeight="normal">
              of {totalItems}
            </Text>
          </HStack>
        </Button>

        <IconButton
          aria-label="Previous page"
          icon={<IconLeftTailedArrow />}
          _disabled={{
            color: 'gray.200',
          }}
          color="gray.600"
          onClick={() => setPage(page - 1)}
          isDisabled={page === 1}
        />
        <IconButton
          aria-label="Next page"
          _disabled={{
            color: 'gray.200',
          }}
          color="gray.600"
          icon={<IconRightTailedArrow />}
          onClick={() => setPage(page + 1)}
          isDisabled={page * pageSize >= totalItems}
        />
      </ButtonGroup>
    </HStack>
  );
};
